import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, FileText } from 'lucide-react';
import api from '../../api/auth.api';

interface ProposalLine {
  line_id: number;
  line_number: number;
  description: string;
  quantity: number | null;
  unit: string | null;
  unit_cost: number | null;
  scheduled_value: number;
}

interface ProposalData {
  proposal_id: number;
  job_id: number;
  file_name: string;
  total_value: number;
  line_count: number;
  job_name: string;
  bradanna_job_number: string | null;
  general_contractor: string | null;
  lines: ProposalLine[];
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatQuantity = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return parseFloat(value.toString()).toString();
};

const JobScopeOfWork: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const jobId = (location.state as any)?.jobId;

  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState<ProposalData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) {
      setError('No job selected');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const response = await api.get(`/api/job-startup/job/${jobId}/scope-of-work`);
        if (response.data.success && response.data.data) {
          setProposal(response.data.data);
        } else {
          setError('No scope of work found for this job');
        }
      } catch (err: any) {
        console.error('Error loading scope of work:', err);
        if (err.response?.status === 404) {
          setError('No scope of work found for this job');
        } else {
          setError('Failed to load scope of work');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [jobId]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        padding: '20px'
      }}>
        <Loader2
          size={48}
          style={{ color: '#3b82f6', animation: 'spin 1s linear infinite' }}
        />
        <div style={{ color: '#64748b', fontSize: 16, marginTop: 16, fontWeight: '500' }}>
          Loading scope of work...
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', paddingBottom: '100px' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: 'transparent',
            border: 'none',
            padding: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px'
          }}
        >
          <ArrowLeft size={22} color="#1e293b" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: '#1e293b', fontSize: '20px', fontWeight: '700', margin: 0 }}>
            Scope of Work
          </h1>
          {proposal?.job_name && (
            <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>
              {proposal.job_name}
            </p>
          )}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Error / Empty state */}
        {error && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#64748b'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#f1f5f9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <FileText size={32} color="#94a3b8" />
            </div>
            <div style={{ fontSize: '16px', fontWeight: '500' }}>
              {error}
            </div>
            <div style={{ fontSize: '14px', marginTop: '8px' }}>
              Import a proposal from the Job Startup page in the admin portal
            </div>
          </div>
        )}

        {proposal && (
          <>
            {/* Summary card */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Line Items
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', marginTop: '2px' }}>
                    {proposal.line_count}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Total Value
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#10b981', marginTop: '2px' }}>
                    {formatCurrency(proposal.total_value)}
                  </div>
                </div>
              </div>
              {proposal.general_contractor && (
                <div style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid #e2e8f0'
                }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    General Contractor
                  </div>
                  <div style={{ fontSize: '15px', fontWeight: '500', color: '#1e293b', marginTop: '2px' }}>
                    {proposal.general_contractor}
                  </div>
                </div>
              )}
            </div>

            {/* Line items */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              {/* Table header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr 70px',
                padding: '10px 14px',
                backgroundColor: '#1e293b',
                gap: '8px'
              }}>
                <span style={{ fontSize: '11px', color: '#ffffff', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  #
                </span>
                <span style={{ fontSize: '11px', color: '#ffffff', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Description
                </span>
                <span style={{ fontSize: '11px', color: '#ffffff', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>
                  Total
                </span>
              </div>

              {/* Line items */}
              {proposal.lines.map((line, idx) => (
                <div
                  key={line.line_id}
                  style={{
                    padding: '12px 14px',
                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                    borderBottom: idx < proposal.lines.length - 1 ? '1px solid #e2e8f0' : 'none'
                  }}
                >
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 1fr 70px',
                    gap: '8px',
                    alignItems: 'flex-start'
                  }}>
                    <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>
                      {line.line_number}
                    </span>
                    <div>
                      <div style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500', lineHeight: '1.4' }}>
                        {line.description}
                      </div>
                      {(line.quantity || line.unit) && (
                        <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                          {line.quantity !== null ? formatQuantity(line.quantity) : ''}{line.unit ? ` ${line.unit}` : ''}
                          {line.unit_cost !== null && ` @ ${formatCurrency(line.unit_cost)}`}
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '13px',
                      color: '#1e293b',
                      fontWeight: '600',
                      textAlign: 'right'
                    }}>
                      {formatCurrency(line.scheduled_value)}
                    </span>
                  </div>
                </div>
              ))}

              {/* Total row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '36px 1fr 70px',
                padding: '14px',
                backgroundColor: '#f0fdf4',
                borderTop: '2px solid #e2e8f0',
                gap: '8px'
              }}>
                <span></span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                  Total ({proposal.lines.length} items)
                </span>
                <span style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#10b981',
                  textAlign: 'right'
                }}>
                  {formatCurrency(proposal.total_value)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default JobScopeOfWork;
